import * as React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Checkbox,
  createTheme
} from '@material-ui/core';
import { ThemeProvider } from '@material-ui/core/styles';
import useMeasuredRef from '../../hooks/useMeasuredRef';
import * as styles from './common.module.scss';
import Scrollbars from 'react-custom-scrollbars';
import { fontFamilies } from '../../theme/text';

interface Props {
  label: string;
  values: any[];
  selectedValues: any[];
  options: string[];
  handleChange: (values: string[]) => void;
}

const materialTheme = createTheme({
  overrides: {
    // @ts-ignore
    MuiFormControl: {
      root: {
        width: 290,
        minHeight: 40,
        height: 'min-content'
      }
    },
    MuiMenuItem: {
      root: {
        fontFamily: fontFamilies.body,
        fontSize: 14,
        letterSpacing: '0.3px',
        color: 'black',
        fontWeight: 300
      }
    },
    MuiChip: {
      root: {
        backgroundColor: '#1987b4',
        height: 25,
        color: 'white',
        marginRight: 6,
        marginBottom: 5
      }
    },
    MuiSelect: {
      selectMenu: {
        whiteSpace: 'inherit',
        maxHeight: 90,
        paddingBottom: 0,
        overflow: 'auto',
        minHeight: 26
      },
      select: {
        '&:focus': {
          backgroundColor: 'white'
        }
      }
    },
    MuiFormLabel: {
      root: {
        fontFamily: fontFamilies.body,
        fontSize: 11,
        letterSpacing: 'normal',
        color: '#4f4f4f !important',
        fontWeight: 300
      }
    },
    MuiOutlinedInput: {
      root: {
        width: '100%',
        height: '100%',
        paddingBottom: 8,
        '&$focused $notchedOutline': {
          borderColor: '#00AAFC'
        }
      },
      input: {
        height: '100% important'
      }
    },
    MuiInputLabel: {
      outlined: {
        fontSize: 14,
        backgroundColor: 'white'
      }
    },
    MuiListItem: {
      gutters: {
        paddingLeft: 10
      }
    }
  }
});

const MultiSelectMenu = React.memo(
  ({ label, values, options, selectedValues, handleChange }: Props) => {
    const { measuredRef, dimensions } = useMeasuredRef([]);

    const renderMenuItems = () => {
      return values.map((item, index) => {
        const isChecked = !!selectedValues.find(checked => checked.value === item.value);
        return (
          <MenuItem key={index} value={item}>
            <Checkbox checked={isChecked} classes={{ root: isChecked ? styles.checkboxActive : styles.checkbox }} />{' '}
            {options[index]}
          </MenuItem>
        );
      });
    };

    return (
      <ThemeProvider theme={materialTheme}>
        <FormControl variant="outlined">
          <InputLabel ref={measuredRef} shrink={true} htmlFor="select-multiple-chip">
            {label}
          </InputLabel>
          <Select
            multiple
            value={selectedValues}
            onChange={event => {
              handleChange(event.target.value as string[]);
            }}
            input={
              <OutlinedInput notched labelWidth={dimensions ? dimensions.width + 20 : 0} id="select-multiple-chip" />
            }
            renderValue={selected => (
              <Scrollbars autoHide={true} universal={true} style={{ minHeight: '26px' }}>
                {(selected as string[]).map((value, index) => (
                  <Chip key={index} label={value.text} />
                ))}
              </Scrollbars>
            )}
          >
            {renderMenuItems()}
          </Select>
        </FormControl>
      </ThemeProvider>
    );
  },
  (prevProps: Props, nextProps: Props) => {
    if (prevProps.selectedValues.length === nextProps.selectedValues.length) {
      return false;
    }
  }
);

export default MultiSelectMenu;
